import {
  type EpocusEncounter,
  type EpocusEncounterStatus,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SearchIcon from "@material-ui/icons/Search";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkToEpocusEncounter } from "../links";
import { encounterDisplayLabel } from "./encounter-display-label";
import { Stethoscope } from "./epocus-icons";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_STATUS_COMPLETE_BG,
  EPOCUS_STATUS_COMPLETE_TEXT,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_STATUS_IMPORTED_BG,
  EPOCUS_STATUS_IMPORTED_TEXT,
  EPOCUS_STATUS_PENDING_BG,
  EPOCUS_STATUS_PENDING_TEXT,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { useDebouncedValue } from "./use-debounced-value";
import {
  RECENT_ENCOUNTERS_INITIAL,
  useRecentEncounters,
} from "./use-recent-encounters";

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
});

const SectionTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  flex: 1,
});

const SectionCountText = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const SearchField = styled(TextField)({
  marginBottom: 12,
  "& .MuiOutlinedInput-root": {
    backgroundColor: EPOCUS_WHITE,
    borderRadius: 10,
  },
  "& .MuiOutlinedInput-input": {
    minHeight: 24,
    fontSize: rem(14),
  },
});

const STATUS_CONFIG: Record<
  EpocusEncounterStatus,
  { label: string; bg: string; text: string }
> = {
  imported: {
    label: "To Document",
    bg: EPOCUS_STATUS_IMPORTED_BG,
    text: EPOCUS_STATUS_IMPORTED_TEXT,
  },
  draft: {
    label: "Draft",
    bg: EPOCUS_STATUS_DRAFT_BG,
    text: EPOCUS_STATUS_DRAFT_TEXT,
  },
  pending_review: {
    label: "Pending Review",
    bg: EPOCUS_STATUS_PENDING_BG,
    text: EPOCUS_STATUS_PENDING_TEXT,
  },
  reviewed: {
    label: "Reviewed",
    bg: EPOCUS_STATUS_REVIEWED_BG,
    text: EPOCUS_STATUS_REVIEWED_TEXT,
  },
  complete: {
    label: "Complete",
    bg: EPOCUS_STATUS_COMPLETE_BG,
    text: EPOCUS_STATUS_COMPLETE_TEXT,
  },
};

const EncounterRow = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: "12px 14px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  border: `1px solid ${EPOCUS_BORDER}`,
  cursor: "pointer",
  "&:hover": { backgroundColor: "#f9fafb" },
});

const EncounterIcon = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor: EPOCUS_BG,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const EncounterInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
});

const EncounterRight = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 4,
  flexShrink: 0,
});

const EncounterPatientName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const EncounterSubtext = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const EncounterTimestamp = styled(Typography)({
  fontSize: rem(11),
  color: EPOCUS_TEXT_SECONDARY,
  whiteSpace: "nowrap",
});

const EmptyStateTitle = styled(Typography)({
  fontSize: rem(15),
  color: EPOCUS_TEXT_SECONDARY,
});

const EmptyStateSubtitle = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 4,
});

const ShowMoreButton = styled(Button)({
  marginTop: 4,
  alignSelf: "center",
  minHeight: 44,
  borderRadius: 10,
  fontSize: rem(13),
  fontWeight: 600,
  textTransform: "none",
  color: EPOCUS_PRIMARY,
});

const formatEncounterDate = (isoDate: string): string => {
  const d = new Date(isoDate);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const RecentEncounterRow: FC<{ encounter: EpocusEncounter }> = ({
  encounter,
}) => {
  const navigate = useNavigate();
  const config =
    STATUS_CONFIG[encounter.status as EpocusEncounterStatus] ??
    STATUS_CONFIG.draft;
  return (
    <EncounterRow
      onClick={() => navigate(linkToEpocusEncounter(encounter.id))}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
          navigate(linkToEpocusEncounter(encounter.id));
        }
        if (e.key === " ") {
          e.preventDefault(); // prevent page scroll on Space
        }
      }}
      onKeyUp={(e: React.KeyboardEvent) => {
        if (e.key === " ") {
          navigate(linkToEpocusEncounter(encounter.id));
        }
      }}
      aria-label={`Open encounter for ${encounterDisplayLabel(encounter)}`}
    >
      <EncounterIcon>
        <Stethoscope
          width={20}
          height={20}
          fill={EPOCUS_ICON_ACTIVE}
          aria-hidden="true"
        />
      </EncounterIcon>
      <EncounterInfo>
        <EncounterPatientName>
          {encounterDisplayLabel(encounter)}
        </EncounterPatientName>
        <EncounterSubtext>
          {encounter.accessionNumber ?? encounter.id}
        </EncounterSubtext>
      </EncounterInfo>
      <EncounterRight>
        <Chip
          label={config.label}
          size="small"
          style={{
            backgroundColor: config.bg,
            color: config.text,
            fontWeight: 600,
            fontSize: rem(11),
            height: 22,
          }}
        />
        <EncounterTimestamp>
          {formatEncounterDate(encounter.date)}
        </EncounterTimestamp>
      </EncounterRight>
      <ChevronRightIcon
        style={{ color: EPOCUS_TEXT_SECONDARY, flexShrink: 0, fontSize: 18 }}
      />
    </EncounterRow>
  );
};

export const RecentEncountersSection: FC<{ encounters: EpocusEncounter[] }> = ({
  encounters,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebouncedValue(searchTerm, 300);

  const { visible, total, hasMore, showMore } = useRecentEncounters({
    encounters,
    searchTerm: debouncedTerm,
  });

  const hasAnyEncounters = useMemo(
    () => encounters.some((e) => e.status !== "imported"),
    [encounters],
  );
  const isSearching = debouncedTerm.trim() !== "";

  const countText =
    total === 0
      ? null
      : isSearching
        ? `Showing ${visible.length} of ${total} result${total === 1 ? "" : "s"}`
        : total > RECENT_ENCOUNTERS_INITIAL
          ? `Showing ${visible.length} of ${total}`
          : null;

  return (
    <Box>
      <SectionHeader>
        <SectionTitle>Recent Encounters</SectionTitle>
        {countText && <SectionCountText>{countText}</SectionCountText>}
      </SectionHeader>

      {!hasAnyEncounters ? (
        <Box style={{ padding: "32px 0", textAlign: "center" }}>
          <EmptyStateTitle>No encounters yet</EmptyStateTitle>
          <EmptyStateSubtitle>
            Tap "New Encounter" to start documenting scans.
          </EmptyStateSubtitle>
        </Box>
      ) : (
        <>
          <SearchField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search encounters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{
              "aria-label": "Search recent encounters",
              autoComplete: "off",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {total === 0 ? (
            <Box style={{ padding: "32px 0", textAlign: "center" }}>
              <EmptyStateTitle>No matching encounters</EmptyStateTitle>
              <EmptyStateSubtitle>
                Try a different name, MRN, accession, or location.
              </EmptyStateSubtitle>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              {visible.map((enc) => (
                <RecentEncounterRow key={enc.id} encounter={enc} />
              ))}
              {hasMore && (
                <ShowMoreButton
                  onClick={showMore}
                  aria-label="Show more encounters"
                >
                  Show more
                </ShowMoreButton>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
